import { Accordion, AccordionSummary, AccordionDetails, Stack, Typography, Divider } from '@mui/material';
import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { TuoteHintarivit, HintarivitAccordion } from '@/components/Hintarivit';
import { QuestionMarkOutlined } from '@mui/icons-material';

const KapasiteettiCard = ({ tuote }) => {
  const { t } = useTranslation();

  return (
    <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary>
        <Typography sx={{ flex: 1 }}>
          { tuote.nimi.maaritelty ? tuote.nimi.tekstit.FIN : t('tuotteet.noName') }
        </Typography>
        <Typography sx={{ textAlign: 'right', flexShrink: 1 }}>
          { (tuote.alkuAika && tuote.loppuAika) ? ` ${dayjs(tuote.alkuAika).format('DD.MM.')}` : '' }
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack divider={<Divider />} spacing={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ArrowBackOutlinedIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.startDate')}
            </Typography>
            <Typography
              sx={{
                minWidth: 0,
                textAlign: 'right',
                flexShrink: 1,
                maxWidth: '50%',
              }}
            >
              {tuote.alkuAika ? `${dayjs(tuote.alkuAika).format('DD.MM.YY HH:mm')}` : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ArrowForwardOutlinedIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.endDate')}
            </Typography>
            <Typography
              sx={{
                minWidth: 0,
                textAlign: 'right',
                flexShrink: 1,
                maxWidth: '50%',
              }}
            >
              {tuote.loppuAika ? `${dayjs(tuote.loppuAika).format('DD.MM.YY HH:mm')}` : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PeopleOutlineIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.personAmount')}
            </Typography>
            <Typography>
              {tuote.henkilomaara ? tuote.henkilomaara : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <HintarivitAccordion tuote={tuote} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const TuoteCard = ({ tuote }) => {
  const { t } = useTranslation();

  return (
    <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary>
        <Typography sx={{ flex: 1 }}>
          { tuote.nimi.maaritelty ? tuote.nimi.tekstit.FIN : t('tuotteet.noName') }
        </Typography>
        <Typography sx={{ textAlign: 'right', flexShrink: 1 }}>
          { (tuote.alkuAika && tuote.loppuAika) ? ` ${dayjs(tuote.alkuAika).format('DD.MM.')}` : '' }
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack divider={<Divider />} spacing={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <QuestionMarkOutlined />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.type')}
            </Typography>
            <Typography>
              {tuote.tyyppi ? tuote.tyyppi : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ArrowBackOutlinedIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.startDate')}
            </Typography>
            <Typography
              sx={{
                minWidth: 0,
                textAlign: 'right',
                flexShrink: 1,
                maxWidth: '50%',
              }}
            >
              {tuote.alkuAika ? `${dayjs(tuote.alkuAika).format('DD.MM.YY HH:mm')}` : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ArrowForwardOutlinedIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.endDate')}
            </Typography>
            <Typography
              sx={{
                minWidth: 0,
                textAlign: 'right',
                flexShrink: 1,
                maxWidth: '50%',
              }}
            >
              {tuote.loppuAika ? `${dayjs(tuote.loppuAika).format('DD.MM.YY HH:mm')}` : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PeopleOutlineIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.personAmount')}
            </Typography>
            <Typography>
              {tuote.henkilomaara ? tuote.henkilomaara : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <HintarivitAccordion tuote={tuote} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const VarausCard = ({ tuote }) => {
  const { t } = useTranslation();

  return (
    <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary>
        <Typography sx={{ flex: 1 }}>
          { tuote.nimi.maaritelty ? tuote.nimi.tekstit.FIN : t('tuotteet.noName') }
        </Typography>
        <Typography sx={{ textAlign: 'right', flexShrink: 1 }}>
          { (tuote.varaus && tuote.varaus.alkuaika && tuote.varaus.loppuaika) ? ` ${dayjs(tuote.varaus.alkuaika).format('DD.MM.')}` : '' }
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack divider={<Divider />} spacing={1}>
          {
            tuote.tyyppi === 'Varaus' && (
              <>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ArrowBackOutlinedIcon />
                  <Typography sx={{ flex: 1 }}>
                    {t('tuotteet.startDate')}
                  </Typography>
                  <Typography
                    sx={{
                      minWidth: 0,
                      textAlign: 'right',
                      flexShrink: 1,
                      maxWidth: '50%',
                    }}
                  >
                    {tuote.varaus.alkuaika ? `${dayjs(tuote.varaus.alkuaika).format('DD.MM.YY HH:mm')}` : t('tuotteet.notSpecified')}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ArrowForwardOutlinedIcon />
                  <Typography sx={{ flex: 1 }}>
                    {t('tuotteet.endDate')}
                  </Typography>
                  <Typography
                    sx={{
                      minWidth: 0,
                      textAlign: 'right',
                      flexShrink: 1,
                      maxWidth: '50%',
                    }}
                  >
                    {tuote.varaus.loppuaika ? `${dayjs(tuote.varaus.loppuaika).format('DD.MM.YY HH:mm')}` : t('tuotteet.notSpecified')}
                  </Typography>
                </Stack>
              </>
            )
          }
          <Stack direction="row" alignItems="center" spacing={2}>
            <NumbersOutlinedIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.amount')}
            </Typography>
            <Typography>
              {tuote.maara ? tuote.maara : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PeopleOutlineIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.personAmount')}
            </Typography>
            <Typography>
              {tuote.henkilomaara ? tuote.henkilomaara : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          {
            tuote.tyyppi === 'Varaus' && (
              <>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CheckCircleOutlinedIcon />
                  <Typography sx={{ flex: 1 }}>
                    {t('tuotteet.confirmed')}
                  </Typography>
                  <Typography>
                    {tuote.varaus.vahvistettu === 1 ? <CheckOutlinedIcon color="success" /> : <DoNotDisturbAltOutlinedIcon />}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CancelOutlinedIcon />
                  <Typography sx={{ flex: 1 }}>
                    {t('tuotteet.cancelled')}
                  </Typography>
                  <Typography>
                    {tuote.varaus.peruutettu === 1 ? <CheckOutlinedIcon color="success" /> : <DoNotDisturbAltOutlinedIcon />}
                  </Typography>
                </Stack>
                <Divider />
                <HintarivitAccordion tuote={tuote} />
              </>
            )
          }
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const DefaultCard = ({ tuote }) => {
  const { t } = useTranslation();

  return (
    <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary>
        <Typography sx={{ flex: 1 }}>
          { tuote.nimi.maaritelty ? tuote.nimi.tekstit.FIN : t('tuotteet.noName') }
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack divider={<Divider />} spacing={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <NumbersOutlinedIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.amount')}
            </Typography>
            <Typography>
              {tuote.maara ? tuote.maara : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <QuestionMarkOutlined />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.type')}
            </Typography>
            <Typography>
              {tuote.tyyppi ? tuote.tyyppi : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PeopleOutlineIcon />
            <Typography sx={{ flex: 1 }}>
              {t('tuotteet.personAmount')}
            </Typography>
            <Typography>
              {tuote.henkilomaara ? tuote.henkilomaara : t('tuotteet.notSpecified')}
            </Typography>
          </Stack>
          <HintarivitAccordion tuote={tuote} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const Tuotteet = ({ tuotteet }) => {
  const { t } = useTranslation();

  if (!tuotteet || tuotteet.length === 0) {
    return (
      <Stack sx={{ marginTop: '1em' }}>
        <Typography color="text.secondary" align="center">
          {t('tuotteet.noProducts')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ marginTop: '1em' }}>
      {
        tuotteet.tuotteet.length > 0 && (
          tuotteet.tuotteet.map((tuote) => {
            if (tuote.tyyppi === 'Kapasiteetti') {
              return <KapasiteettiCard key={tuote.id.tuoteId} tuote={tuote} />;
            }
            else if (tuote.tyyppi === 'Varaus') {
              return <VarausCard key={tuote.id.tuoteId} tuote={tuote} />;
            }
            else if (tuote.tyyppi === 'Tapahtuma') {
              return <TuoteCard key={tuote.id.tuoteId} tuote={tuote} />;
            }
            else {
              return <DefaultCard key={tuote.id.tuoteId} tuote={tuote} />;
            }
          })
        )
      }
    </Stack>
  );
};

export default Tuotteet;
